import { useQuery } from 'react-query';
import axiosInstance from '../config/Axios';
import { useEntitlements } from '../hooks/useEntitlements';

/**
 * Phase 6 UX — "what's playing now + next transition" for a wall or group, computed
 * server-side with the SAME rules as playback resolution (so the display can't
 * disagree with what actually plays).
 *   GET /video-walls/{id}/schedule-status   → { schedule: { timezone, now, next } }
 *   GET /wall-groups/{id}/schedule-status    → { schedule, member_count, uniform }
 *
 * Backend wraps in a Laravel resource ({ data: { status, ... } }); unwrap with
 * `res?.data ?? res`. Polls every 30s so the "now/next" stays fresh as windows flip.
 */
export const useScheduleStatus = ({ type = 'wall', id } = {}) =>
  useQuery(
    ['scheduleStatus', type, id],
    async () => {
      const base = type === 'group' ? 'wall-groups' : 'video-walls';
      const res = (await axiosInstance.get(`/${base}/${id}/schedule-status`)).data;
      return res?.data ?? res; // { schedule, member_count?, uniform? }
    },
    { enabled: !!id, staleTime: 15_000, refetchInterval: 30_000 },
  );

/**
 * Café equivalent — "now playing + next transition" for a device/group, computed
 * server-side with the SAME shared engine (ScheduleResolver + ScheduleStatusService)
 * as the wall, so the timeline can't disagree with what actually plays.
 *   GET /campaign-schedules/status?device_id=|group_id=  → { schedule, uniform }
 * Here `now.source = 'none'` means the device's default EVENT theme is showing.
 */
export const useCafeScheduleStatus = ({ type = 'device', id } = {}) => {
  // /campaign-schedules/status is a scheduling_pro route: a package that explicitly lacks it is not asked.
  const { has } = useEntitlements();
  return useQuery(
    ['cafeScheduleStatus', type, id],
    async () => {
      const params = type === 'group' ? { group_id: id } : { device_id: id };
      const res = (await axiosInstance.get('/campaign-schedules/status', { params })).data;
      return res?.data ?? res; // { schedule: { timezone, now, next }, uniform }
    },
    { enabled: !!id && has('scheduling_pro'), staleTime: 15_000, refetchInterval: 30_000 },
  );
};
